import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PodhiveLogo } from "@/components/taxpeerlogo.component";
import AuthService from "@/lib/auth/AuthApi.service";
import { AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/contexts/auth.context";
import useAltchaAutoToken from "@/lib/captcha/useAltchaAutoToken.hook";
import { AltchaWidget } from "@/lib/captcha/AltchaWidget.component";
import useDeferredExecution from "@/lib/hooks/useDeferredExecution.hook";
import { AnalyticsService } from "@/lib/analytics/analytics.service";
import { captureAuthException } from "@/lib/auth/captureAuthException";
import { isInAppBrowserAuthError } from "@/lib/auth/inAppBrowser";
import { InAppBrowserBanner } from "../../components/InAppBrowserBanner.component";
import { useInAppBrowser } from "@/lib/hooks/useInAppBrowser.hook";
import { savePendingUpload } from "@/lib/onboarding/pendingUpload";
import ChapterPreviewCard, {
	type ChapterPreview,
} from "./ChapterPreviewCard.component";
import ExtractionProgress from "./ExtractionProgress.component";

type ExtractionStatus = "idle" | "running" | "done" | "failed";

export default function FinishSignupPage() {
	const { t } = useTranslation("common");
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const storageFileId = searchParams.get("storageFileId");
	const { setAuthToken } = useAuth();
	const { isInAppBrowser, openInExternalBrowser } = useInAppBrowser();

	// Anonymous session token — the signup form is usable as soon as this
	// exists, independently of how the extraction is doing.
	const [anonymousToken, setAnonymousToken] = useState<string | null>(null);
	const [extractionStatus, setExtractionStatus] = useState<ExtractionStatus>(
		storageFileId ? "idle" : "done",
	);
	const [chapters, setChapters] = useState<ChapterPreview[]>([]);
	const [errorDismissed, setErrorDismissed] = useState(false);

	// Signup form state
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [submitError, setSubmitError] = useState<string | null>(null);

	const initStartedRef = useRef(false);
	const extractionAttemptRef = useRef(0);
	const formViewedTrackedRef = useRef(false);
	const formStartedTrackedRef = useRef(false);
	const emailInputRef = useRef<HTMLInputElement>(null);

	const { widgetRef, hasActiveToken } = useAltchaAutoToken({
		action: "login",
		intervalMs: 90_000,
	});

	// Survive an interrupted signup: if the user ends up logging in instead of
	// completing the funnel, the dashboard can still resume this upload.
	useEffect(() => {
		if (storageFileId) savePendingUpload(storageFileId);
	}, [storageFileId]);

	const extractChapters = useCallback(
		async (token: string) => {
			if (!storageFileId) return;

			extractionAttemptRef.current += 1;
			setErrorDismissed(false);
			setExtractionStatus("running");
			AnalyticsService.track("signup_funnel_extraction_started", {
				storage_file_id: storageFileId,
				attempt: extractionAttemptRef.current,
			});

			try {
				const baseUrl = import.meta.env.VITE_API_URL || "http://localhost:3000";
				const response = await fetch(
					`${baseUrl}/api/v1/anonymous/extract-chapters`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${token}`,
						},
						body: JSON.stringify({ storageFileId }),
					},
				);

				if (!response.ok) {
					throw new Error("Failed to extract chapters");
				}

				const data = await response.json();
				const extractedChapters = data.chapters || [];
				setChapters(extractedChapters);

				// Track successful chapter extraction
				AnalyticsService.track("signup_funnel_chapters_ready", {
					chapter_count: extractedChapters.length,
					storage_file_id: storageFileId,
				});

				setExtractionStatus("done");
			} catch (error) {
				console.error("Chapter extraction failed:", error);

				// Track extraction failure
				AnalyticsService.track("signup_funnel_extraction_failed", {
					storage_file_id: storageFileId,
					error_message:
						error instanceof Error ? error.message : "Unknown error",
				});

				setExtractionStatus("failed");
			}
		},
		[storageFileId],
	);

	const startAnonymousSession = useCallback(async () => {
		try {
			const authService = AuthService.getInstance();

			// Check if already authenticated with Firebase
			let token = "";
			const hash = typeof window !== "undefined" ? window.location.hash : "";
			const hashParams = new URLSearchParams(
				hash.startsWith("#") ? hash.slice(1) : hash,
			);
			const customToken = hashParams.get("ct");

			if (customToken) {
				// Continue the same Firebase UID across domains (landing page -> frontend)
				// without creating a 2nd anonymous user.
				token =
					await authService.signInWithCustomTokenFirebaseOnly(customToken);

				// Remove token from URL immediately to prevent reuse/leakage.
				hashParams.delete("ct");
				const newHash = hashParams.toString();
				const newUrl =
					window.location.pathname +
					window.location.search +
					(newHash ? `#${newHash}` : "");
				window.history.replaceState(null, "", newUrl);
			} else if (authService.auth.currentUser) {
				console.log("User already authenticated with Firebase, proceeding...");
				token = await authService.auth.currentUser.getIdToken();
			} else {
				// Perform anonymous login only to Firebase
				const result = await authService.loginAnonymously();
				token = result.token;
				console.log("Logged in anonymously to Firebase");
			}

			setAnonymousToken(token);

			// Kick off the extraction, but don't block the signup form on it —
			// the user can enter their details while chapters are detected.
			if (storageFileId) {
				void extractChapters(token);
			}
		} catch (error) {
			console.error("Failed to initialize anonymous session:", error);
			captureAuthException(error, "finish_signup_anonymous_init");
			AnalyticsService.track("signup_funnel_extraction_failed", {
				storage_file_id: storageFileId,
				error_message: error instanceof Error ? error.message : "Unknown error",
			});
			setExtractionStatus("failed");
		}
	}, [extractChapters, storageFileId]);

	useEffect(() => {
		if (initStartedRef.current) return;
		initStartedRef.current = true;
		void startAnonymousSession();
	}, [startAnonymousSession]);

	// The signup form counts as "viewed" as soon as it is interactive.
	useEffect(() => {
		if (!anonymousToken || formViewedTrackedRef.current) return;
		formViewedTrackedRef.current = true;
		AnalyticsService.track("signup_funnel_form_viewed", {
			chapter_count: chapters.length,
			storage_file_id: storageFileId,
		});
	}, [anonymousToken, chapters.length, storageFileId]);

	const trackFormStarted = () => {
		if (formStartedTrackedRef.current) return;
		formStartedTrackedRef.current = true;
		AnalyticsService.track("signup_funnel_form_started", {
			storage_file_id: storageFileId,
		});
	};

	const handleRetryExtraction = () => {
		AnalyticsService.track("signup_funnel_extraction_retry", {
			storage_file_id: storageFileId,
			attempt: extractionAttemptRef.current + 1,
		});
		if (anonymousToken) {
			void extractChapters(anonymousToken);
		} else {
			// Auth itself failed — retry the whole session bootstrap.
			void startAnonymousSession();
		}
	};

	const handleContinueWithoutPreview = () => {
		AnalyticsService.track("signup_funnel_fallback_signup", {
			storage_file_id: storageFileId,
		});
		setErrorDismissed(true);
		emailInputRef.current?.focus();
	};

	const executeUpgradeAccount = async () => {
		if (!email || !password) return;

		setSubmitError(null);

		try {
			const authService = AuthService.getInstance();
			const response = await authService.upgradeAnonymousAccount({
				email,
				password,
			});

			// Sync upgraded email + isAnonymous flag to backend (no re-login required)
			await authService.syncUpgradedAccountToBackend(email);

			// Set the auth token to log the user in fully
			await setAuthToken(response.token);

			// Track successful signup completion
			AnalyticsService.track("signup_funnel_completed", {
				signup_method: "email",
				chapter_count: chapters.length,
				storage_file_id: storageFileId,
			});

			// Redirect to dashboard with storageFileId to trigger workspace creation
			if (storageFileId) {
				navigate(`/dashboard?storageFileId=${storageFileId}`);
			} else {
				navigate("/dashboard");
			}
		} catch (error) {
			console.error("Upgrade failed:", error);
			captureAuthException(error, "finish_signup_upgrade_email");
			setSubmitError(
				typeof error === "string" ? error : t("finishSignup.form.genericError"),
			);
		}
	};

	const { run: runUpgradeAccount, isBusy: isUpgradeBusy } =
		useDeferredExecution({
			condition: hasActiveToken,
			canRun: Boolean(email && password),
			timeoutMs: 15_000,
			onTimeout: () => setSubmitError(t("captcha.botDetected")),
			execute: executeUpgradeAccount,
		});

	const handleUpgradeAccount = (e: React.FormEvent) => {
		e.preventDefault();
		AnalyticsService.track("signup_funnel_details_submitted", {
			signup_method: "email",
			chapter_count: chapters.length,
			storage_file_id: storageFileId,
			extraction_status: extractionStatus,
		});
		runUpgradeAccount();
	};

	const executeGoogleUpgrade = async () => {
		setSubmitError(null);

		try {
			const authService = AuthService.getInstance();
			const response = await authService.upgradeAnonymousAccountWithGoogle();

			// Sync upgraded email + isAnonymous flag to backend (uses current Firebase user's email)
			await authService.syncUpgradedAccountToBackend();

			await setAuthToken(response.token);

			// Track successful signup completion
			AnalyticsService.track("signup_funnel_completed", {
				signup_method: "google",
				chapter_count: chapters.length,
				storage_file_id: storageFileId,
			});

			// Redirect to dashboard with storageFileId to trigger workspace creation
			if (storageFileId) {
				navigate(`/dashboard?storageFileId=${storageFileId}`);
			} else {
				navigate("/dashboard");
			}
		} catch (error) {
			if (isInAppBrowserAuthError(error)) {
				return;
			}
			console.error("Google upgrade failed:", error);
			captureAuthException(error, "finish_signup_upgrade_google");
			setSubmitError(
				typeof error === "string" ? error : t("finishSignup.form.googleError"),
			);
		}
	};

	const { run: runGoogleUpgrade, isBusy: isGoogleBusy } = useDeferredExecution({
		condition: hasActiveToken,
		timeoutMs: 15_000,
		onTimeout: () => setSubmitError(t("captcha.botDetected")),
		execute: executeGoogleUpgrade,
	});

	const handleGoogleUpgrade = () => {
		if (isInAppBrowser) {
			openInExternalBrowser(window.location.href, "oauth");
			return;
		}
		AnalyticsService.track("signup_funnel_details_submitted", {
			signup_method: "google",
			chapter_count: chapters.length,
			storage_file_id: storageFileId,
			extraction_status: extractionStatus,
		});
		runGoogleUpgrade();
	};

	const isSubmitting = isUpgradeBusy || isGoogleBusy;

	const isInitializing = !anonymousToken && extractionStatus !== "failed";
	const isExtracting =
		extractionStatus === "running" ||
		(extractionStatus === "idle" && Boolean(storageFileId));
	const showChapterPreview = extractionStatus === "done" && chapters.length > 0;
	const showExtractionError = extractionStatus === "failed" && !errorDismissed;

	const headerTitle = isExtracting
		? t("finishSignup.extracting.title")
		: showChapterPreview
			? t("finishSignup.ready.title")
			: t("finishSignup.noPreview.title");
	const headerSubtitle = isExtracting
		? t("finishSignup.extracting.subtitle")
		: showChapterPreview
			? t("finishSignup.ready.subtitle")
			: t("finishSignup.noPreview.subtitle");

	return (
		<div className="flex min-h-screen flex-col items-center justify-center p-4">
			<Card className="w-full max-w-[600px] border-none">
				<CardHeader className="text-center pb-8">
					<div className="mx-auto mb-6 flex justify-center">
						<PodhiveLogo bildmarke={true} width={120} />
					</div>
					<CardTitle className="text-3xl font-bold mb-2">
						{headerTitle}
					</CardTitle>
					<p className="text-muted-foreground">{headerSubtitle}</p>
				</CardHeader>
				<CardContent>
					<div className="flex flex-col items-center gap-6">
						{isInitializing && !isExtracting && (
							<div className="flex flex-col items-center gap-4 py-8">
								<Loader2 className="h-8 w-8 animate-spin text-primary" />
								<p className="text-sm text-muted-foreground">
									{t("finishSignup.initializing")}
								</p>
							</div>
						)}

						{isExtracting && <ExtractionProgress />}

						{showChapterPreview && <ChapterPreviewCard chapters={chapters} />}

						{showExtractionError && (
							<div className="w-full space-y-4 rounded-lg border border-destructive/30 bg-destructive/5 p-4 animate-in fade-in duration-500">
								<div className="flex items-start gap-3">
									<AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
									<div className="space-y-1">
										<p className="text-sm font-semibold">
											{t("finishSignup.error.title")}
										</p>
										<p className="text-sm text-muted-foreground">
											{t("finishSignup.error.description")}
										</p>
									</div>
								</div>
								<div className="flex flex-col gap-2 sm:flex-row">
									<Button
										variant="outline"
										className="w-full sm:w-auto"
										onClick={handleRetryExtraction}
									>
										<RefreshCw className="mr-2 h-4 w-4" />
										{t("finishSignup.error.retry")}
									</Button>
									{anonymousToken && (
										<Button
											variant="ghost"
											className="w-full text-muted-foreground sm:w-auto"
											onClick={handleContinueWithoutPreview}
										>
											{t("finishSignup.error.continueWithoutPreview")}
										</Button>
									)}
								</div>
							</div>
						)}

						{/* Signup form — available while the extraction is still running so
						    the wait time doubles as form-filling time. */}
						{anonymousToken && (
							<div className="w-full max-w-sm space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
								<InAppBrowserBanner />
								<form onSubmit={handleUpgradeAccount} className="space-y-4">
									<div className="space-y-2">
										<Label htmlFor="email">
											{t("finishSignup.form.email")}
										</Label>
										<Input
											ref={emailInputRef}
											id="email"
											type="email"
											value={email}
											onChange={(e) => {
												trackFormStarted();
												setEmail(e.target.value);
											}}
											placeholder="alex@example.com"
											required
											disabled={isSubmitting}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="password">
											{t("finishSignup.form.password")}
										</Label>
										<Input
											id="password"
											type="password"
											value={password}
											onChange={(e) => {
												trackFormStarted();
												setPassword(e.target.value);
											}}
											required
											disabled={isSubmitting}
											minLength={6}
										/>
										<p className="text-xs text-muted-foreground">
											{t("finishSignup.form.passwordHint")}
										</p>
									</div>

									{submitError && (
										<div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm flex items-center gap-2">
											<AlertCircle className="h-4 w-4" />
											{submitError}
										</div>
									)}

									<Button
										type="submit"
										className="w-full mt-2"
										size="lg"
										disabled={isSubmitting}
									>
										{isSubmitting ? (
											<>
												<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												{t("finishSignup.form.submitting")}
											</>
										) : (
											t("finishSignup.form.submit")
										)}
									</Button>

									{isExtracting && (
										<p className="text-xs text-center text-muted-foreground">
											{t("finishSignup.form.extractionStillRunning")}
										</p>
									)}
								</form>

								<div className="relative">
									<div className="absolute inset-0 flex items-center">
										<span className="w-full border-t" />
									</div>
									<div className="relative flex justify-center text-xs uppercase">
										<span className="bg-transparent px-2 text-muted-foreground">
											{t("finishSignup.form.orContinueWith")}
										</span>
									</div>
								</div>

								<Button
									variant="outline"
									className="w-full"
									onClick={handleGoogleUpgrade}
									disabled={isSubmitting || !hasActiveToken}
								>
									{isSubmitting ? (
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									) : (
										<div className="flex items-center gap-2">
											<svg
												className="h-4 w-4"
												viewBox="0 0 24 24"
												role="img"
												aria-label="Google Logo"
											>
												<title>Google Logo</title>
												<path
													d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
													fill="#4285F4"
												/>
												<path
													d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
													fill="#34A853"
												/>
												<path
													d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
													fill="#FBBC05"
												/>
												<path
													d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
													fill="#EA4335"
												/>
											</svg>
											Google
										</div>
									)}
								</Button>

								<p className="text-xs text-center text-muted-foreground">
									{t("finishSignup.form.free")}
								</p>
							</div>
						)}
					</div>

					<div className="mt-4">
						<AltchaWidget
							widgetRef={widgetRef}
							hasActiveToken={hasActiveToken}
						/>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
